import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import PageHeader from '../components/shared/PageHeader';
import Card from '../components/shared/Card';
import Button from '../components/shared/Button';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { projectsData } from '../data/projects';

const { FiExternalLink, FiArrowRight, FiFilter, FiGrid, FiList } = FiIcons;

const Projects = () => {
  const [filter, setFilter] = useState('All');
  const [viewMode, setViewMode] = useState('grid');

  const categories = ['All', ...new Set(projectsData.map(project => project.category))];
  const filteredProjects = filter === 'All' 
    ? projectsData 
    : projectsData.filter(project => project.category === filter);

  const stats = [
    { value: "50+", label: "Projects Delivered" },
    { value: "100%", label: "Client Satisfaction" },
    { value: "Local", label: "Aylesbury Focus" }
  ];

  return (
    <div className="min-h-screen bg-white">
      <PageHeader
        title="Our Projects"
        subtitle="Discover how we've helped local Aylesbury businesses transform their online presence and achieve remarkable growth through our tailored digital solutions."
        stats={stats}
        className="pt-32"
      />

      {/* Filter and View Controls */}
      <section className="py-12 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            {/* Category Filter */}
            <div className="flex flex-wrap gap-2">
              <SafeIcon icon={FiFilter} className="text-slate-600 mt-2 mr-2" />
              {categories.map((category) => (
                <Button
                  key={category}
                  variant={filter === category ? "primary" : "outline"}
                  size="sm"
                  onClick={() => setFilter(category)}
                  animate={false}
                >
                  {category}
                </Button>
              ))}
            </div>

            {/* View Mode Toggle */}
            <div className="flex items-center space-x-2 bg-white rounded-lg p-1 border border-slate-200">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded transition-colors duration-300 ${
                  viewMode === 'grid' ? 'bg-orange-500 text-white' : 'text-slate-600'
                }`}
              >
                <SafeIcon icon={FiGrid} />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded transition-colors duration-300 ${
                  viewMode === 'list' ? 'bg-orange-500 text-white' : 'text-slate-600'
                }`}
              >
                <SafeIcon icon={FiList} />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Projects Grid/List */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {filteredProjects.length === 0 ? (
            <div className="text-center py-16">
              <div className="text-slate-400 text-xl mb-4">No projects found in this category</div>
              <Button
                variant="ghost"
                onClick={() => setFilter('All')}
              >
                View All Projects
              </Button>
            </div>
          ) : (
            <div className={viewMode === 'grid' ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" : "space-y-8"}>
              {filteredProjects.map((project, index) => (
                <motion.div
                  key={project.id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                >
                  <Card
                    title={project.title}
                    description={project.shortDescription}
                    image={project.image}
                    badge={project.category}
                    tags={project.tags.slice(0, 3)}
                    result={project.results[0]}
                    link={{
                      to: `/projects/${project.slug}`,
                      text: "View Details"
                    }}
                    className={viewMode === 'list' ? 'flex flex-col md:flex-row' : ''}
                  >
                    {project.url && (
                      <div 
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          window.open(project.url, '_blank', 'noopener,noreferrer');
                        }}
                        className="flex items-center space-x-1 text-slate-500 hover:text-slate-700 text-sm transition-colors duration-300 cursor-pointer"
                      >
                        <SafeIcon icon={FiExternalLink} className="text-xs" />
                        <span>Live Site</span>
                      </div>
                    )}
                  </Card>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Ready to Start Your Project?
            </h2>
            <p className="text-xl text-slate-300 mb-8">
              Let's discuss how we can help transform your business with a website that delivers real results.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                variant="primary"
                size="lg"
                to="/contact"
                icon={FiArrowRight}
              >
                Start Your Project
              </Button>
              {/* <Button
                variant="outline"
                size="lg"
                href="tel:+447769061291"
              >
                Call: 07769061291
              </Button> */}
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Projects;